import React, { FC, useRef, useState } from "react";

import { Button, Icon, Popover, SheetContainer, Text } from "@bookingcom/bui-react";
import { CrossIcon, InfoSignIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import Frame from "../../../components/elements/Frame";
import styles from "./FareFeature.module.css";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useUserAgent from "../../../hooks/useUserAgent";
import { TriggerAttributes } from "@bookingcom/bui-react/components/_base/Flyout/Flyout.types";
import { UIBrandedFareFeatureAvailability } from "@flights/types";
import FareFeatureIcon from "../FareFeatureIcon/FareFeatureIcon";
import { ArrowUpBoldIcon } from "@bookingcom/bui-assets-react/streamline";

export const FareFeature: FC<{
  content: string;
  IconSVG?: FC;
  id?: string;
  description?: string;
  subtitle?: string;
  availability: UIBrandedFareFeatureAvailability;
  compactHeight?: boolean;
  noMargin?: boolean;
  isUniqueUpsellFeature?: boolean;
  showUpsellFeatureUpgradeIcon?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- this line was auto generated, hence fix the issue timely
}> = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IconSVG,
  content,
  id,
  description,
  subtitle,
  availability,
  compactHeight,
  noMargin,
  isUniqueUpsellFeature,
  showUpsellFeatureUpgradeIcon
}) => {
  const [showDescription, setShowDescription] = useState(false);
  const i18n = useI18n();
  const { isMobile } = useUserAgent();
  const popoverRef = useRef(document.body);
  const hasUpgradeFareIcon = isUniqueUpsellFeature && showUpsellFeatureUpgradeIcon;

  IconSVG = availability === "NOT_INCLUDED" ? CrossIcon : IconSVG;

  if (!IconSVG) return null;

  const renderDescriptionButton = (attributes?: TriggerAttributes) => (
    <Button
      variant="tertiary-neutral"
      onClick={() => isMobile && setShowDescription(true)}
      attributes={{
        ...attributes,
        "aria-label": i18n.trans(t("a11y_flights_apex_bf_greenfare_tooltip_open"))
      }}
    >
      <Icon size="medium" svg={InfoSignIcon} color="neutral_alt" />
    </Button>
  );

  const renderDescriptionContent = () => <Text>{description}</Text>;

  return (
    <Frame
      direction="row"
      mb={noMargin ? 0 : compactHeight ? 2 : 3}
      className={styles.featureWrapper}
      alignItems="center"
    >
      <FareFeatureIcon
        icon={IconSVG}
        availability={availability}
        color={isUniqueUpsellFeature ? "constructive" : undefined}
      />
      <div className={styles.featureContent}>
        <Text
          variant={isUniqueUpsellFeature ? "strong_2" : "body_2"}
          attributes={{
            "data-testid": id && `branded_fare_feature_${id}_${availability}`,
            "aria-label": `${content}. ${
              hasUpgradeFareIcon ? i18n.trans(t("a11y_flights_bf_upsell_modal_upgrade_icon")) : ""
            }`
          }}
          color={isUniqueUpsellFeature ? "constructive" : undefined}
        >
          {content}
        </Text>
        {subtitle ? <Text variant="body_2">{subtitle}</Text> : null}
      </div>
      {hasUpgradeFareIcon && <Icon size={"medium"} svg={ArrowUpBoldIcon} color={"constructive"} />}

      {description && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span onClick={(event) => event.stopPropagation()}>
          {isMobile ? (
            <>
              {renderDescriptionButton()}
              <SheetContainer
                active={showDescription}
                closeAriaLabel={i18n.trans(t("a11y_flights_apex_bf_greenfare_tooltip_close"))}
                title={i18n.trans(t("flights_apex_bf_greenfare_tooltip_hover_title"))}
                position="bottom"
                onCloseTrigger={() => setShowDescription(false)}
              >
                {renderDescriptionContent()}
              </SheetContainer>
            </>
          ) : (
            <Popover
              containerRef={popoverRef}
              closeAriaLabel={i18n.trans(t("a11y_flights_apex_bf_greenfare_tooltip_close"))}
            >
              <Popover.Trigger className={styles.infoIcon}>{renderDescriptionButton}</Popover.Trigger>
              <Popover.Content>{renderDescriptionContent()}</Popover.Content>
            </Popover>
          )}
        </span>
      )}
    </Frame>
  );
};

export default FareFeature;
